import { useEffect, useState } from "react";
import { weeklyReport, exportCsvUrl, exportXlsxUrl, exportPivotXlsxUrl } from "@/lib/api";
import { formatINR, todayISO } from "@/lib/format";
import { waLink, buildWeekMessage, buildBoyWeekMessage } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Download, FileSpreadsheet, CalendarDays, Grid3x3, MessageCircle } from "lucide-react";
import { useBranch } from "@/context/BranchContext";

const WEEKS_OPTIONS = [4, 8, 12, 26, 52];

export default function WeeklyReport() {
  const [weeks, setWeeks] = useState(8);
  const [endDate, setEndDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { branchId } = useBranch();

  const load = async () => {
    setLoading(true);
    try {
      setData(await weeklyReport({ weeks, end_date: endDate }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks, endDate, branchId]);

  const rangeCsvHref = exportCsvUrl({
    start_date: data?.range?.start,
    end_date: data?.range?.end,
  });
  const rangeXlsxHref = exportXlsxUrl({
    start_date: data?.range?.start,
    end_date: data?.range?.end,
  });
  const rangePivotHref = exportPivotXlsxUrl({
    start_date: data?.range?.start,
    end_date: data?.range?.end,
    group_by: "week",
  });

  const rows = data?.weeks || [];
  const nonEmpty = rows.filter((w) => w.entries > 0);

  const grandTotals = rows.reduce(
    (a, w) => ({
      cash_collected: a.cash_collected + w.cash_collected,
      final_payable: a.final_payable + w.final_payable,
      paid_amount: a.paid_amount + w.paid_amount,
      balance: a.balance + w.balance,
      min_guarantee_applied: a.min_guarantee_applied + w.min_guarantee_applied,
    }),
    { cash_collected: 0, final_payable: 0, paid_amount: 0, balance: 0, min_guarantee_applied: 0 }
  );

  return (
    <div className="space-y-6" data-testid="weekly-report-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Analytics</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1">
            Weekly Report
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Week-by-week (Mon–Sun) totals with per-delivery-boy breakdown. Expand a week to see details.
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1">
            <label className="text-xs text-zinc-500">Show last</label>
            <Select value={String(weeks)} onValueChange={(v) => setWeeks(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="weekly-weeks-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {WEEKS_OPTIONS.map((w) => (
                  <SelectItem key={w} value={String(w)}>
                    {w} weeks
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-zinc-500">Ending</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-44"
              data-testid="weekly-end-date"
            />
          </div>
          <a href={rangePivotHref} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-lg" data-testid="weekly-pivot-xlsx-btn">
              <Grid3x3 className="h-4 w-4 mr-1.5" /> Pivot Excel
            </Button>
          </a>
          <a href={rangeXlsxHref} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-lg" data-testid="weekly-export-xlsx-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
            </Button>
          </a>
          <a href={rangeCsvHref} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-lg" data-testid="weekly-export-csv-btn">
              <Download className="h-4 w-4 mr-1.5" /> CSV
            </Button>
          </a>
        </div>
      </div>

      {/* Range totals */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard label="Cash Collected" value={grandTotals.cash_collected} />
        <StatCard label="Final Payable" value={grandTotals.final_payable} />
        <StatCard label="Paid" value={grandTotals.paid_amount} tone="positive" />
        <StatCard label="Balance" value={grandTotals.balance} tone="warning" />
        <StatCard label="Min Guarantee" value={grandTotals.min_guarantee_applied} tone="danger" />
      </div>

      {/* Weeks list */}
      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        {loading && <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>}
        {!loading && nonEmpty.length === 0 && (
          <div className="p-14 text-center">
            <CalendarDays className="h-8 w-8 text-zinc-300 mx-auto" />
            <div className="mt-2 font-medium text-zinc-700">No collection data in this range</div>
            <div className="text-xs text-zinc-500">Add some entries or widen the range.</div>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <Accordion type="multiple" className="w-full">
            {rows.map((w, idx) => {
              const isEmpty = w.entries === 0;
              return (
                <AccordionItem
                  key={w.week_start}
                  value={w.week_start}
                  className={`border-b border-zinc-100 ${isEmpty ? "opacity-60" : ""}`}
                  data-testid={`weekly-item-${w.week_start}`}
                >
                  <AccordionTrigger className="px-4 py-3 hover:bg-zinc-50 hover:no-underline">
                    <div className="flex-1 flex items-center gap-4 tabular text-left">
                      <div className="min-w-[180px]">
                        <div className="font-medium text-zinc-950 text-sm">{w.label}</div>
                        <div className="text-xs text-zinc-500">
                          {w.entries} {w.entries === 1 ? "entry" : "entries"}
                          {idx === 0 && (
                            <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold uppercase tracking-wider">
                              Current
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="hidden md:grid grid-cols-4 gap-6 flex-1 pr-4">
                        <MiniStat label="Cash" value={w.cash_collected} />
                        <MiniStat label="Payable" value={w.final_payable} />
                        <MiniStat label="Paid" value={w.paid_amount} tone="positive" />
                        <MiniStat
                          label="Balance"
                          value={w.balance}
                          tone={w.balance > 0 ? "warning" : "default"}
                        />
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="bg-zinc-50/40">
                    {isEmpty ? (
                      <div className="px-6 py-4 text-sm text-zinc-500">No entries recorded this week.</div>
                    ) : (
                      <div className="px-2 md:px-4 py-3">
                        <div className="grid md:hidden grid-cols-2 gap-3 pb-3 tabular">
                          <MiniStat label="Cash" value={w.cash_collected} />
                          <MiniStat label="Payable" value={w.final_payable} />
                          <MiniStat label="Paid" value={w.paid_amount} tone="positive" />
                          <MiniStat label="Balance" value={w.balance} tone="warning" />
                        </div>

                        {/* Payment method breakdown */}
                        <div className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-2 tabular">
                          <PmChip label="Cash" value={(w.by_payment_method || {}).cash} tint="bg-emerald-50 text-emerald-700 border-emerald-200" />
                          <PmChip label="UPI" value={(w.by_payment_method || {}).upi} tint="bg-blue-50 text-blue-700 border-blue-200" />
                          <PmChip label="Bank" value={(w.by_payment_method || {}).bank} tint="bg-violet-50 text-violet-700 border-violet-200" />
                          <PmChip label="Other" value={(w.by_payment_method || {}).other} tint="bg-zinc-50 text-zinc-700 border-zinc-200" />
                        </div>

                        <div className="bg-white rounded-lg border border-zinc-200 overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-zinc-50 border-b border-zinc-200">
                              <tr className="text-left">
                                <Th>Delivery Boy</Th>
                                <Th className="text-right">Entries</Th>
                                <Th className="text-right">Cash</Th>
                                <Th className="text-right">Earning</Th>
                                <Th className="text-right">Payable</Th>
                                <Th className="text-right">Paid</Th>
                                <Th className="text-right">Balance</Th>
                                <Th className="text-right">MG</Th>
                                <Th className="text-right">Share / Excel</Th>
                              </tr>
                            </thead>
                            <tbody>
                              {w.per_delivery_boy.map((p) => (
                                <tr
                                  key={p.delivery_boy_id}
                                  className="border-b border-zinc-100 last:border-b-0 tabular"
                                >
                                  <td className="px-4 py-2.5 font-medium text-zinc-900">
                                    {p.delivery_boy_name || "—"}
                                  </td>
                                  <td className="px-4 py-2.5 text-right">{p.entries}</td>
                                  <td className="px-4 py-2.5 text-right">{formatINR(p.cash_collected)}</td>
                                  <td className="px-4 py-2.5 text-right">{formatINR(p.earning)}</td>
                                  <td className="px-4 py-2.5 text-right font-semibold">
                                    {formatINR(p.final_payable)}
                                  </td>
                                  <td className="px-4 py-2.5 text-right text-emerald-700">
                                    {formatINR(p.paid_amount)}
                                  </td>
                                  <td
                                    className={`px-4 py-2.5 text-right font-semibold ${
                                      p.balance > 0 ? "text-amber-700" : "text-emerald-700"
                                    }`}
                                  >
                                    {formatINR(p.balance)}
                                  </td>
                                  <td className="px-4 py-2.5 text-right text-rose-600">
                                    {formatINR(p.min_guarantee_applied)}
                                  </td>
                                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                    <a
                                      href={waLink(p.delivery_boy_phone, buildBoyWeekMessage(w, p))}
                                      target="_blank"
                                      rel="noreferrer"
                                      title={p.delivery_boy_phone ? `Send to ${p.delivery_boy_phone}` : "Share via WhatsApp"}
                                    >
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-emerald-700"
                                        data-testid={`wa-boy-${p.delivery_boy_id}-${w.week_start}`}
                                      >
                                        <MessageCircle className="h-3.5 w-3.5" />
                                      </Button>
                                    </a>
                                    <a
                                      href={exportXlsxUrl({
                                        start_date: w.week_start,
                                        end_date: w.week_end,
                                        delivery_boy_id: p.delivery_boy_id,
                                      })}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <Button size="sm" variant="ghost" className="text-emerald-700">
                                        <FileSpreadsheet className="h-3.5 w-3.5" />
                                      </Button>
                                    </a>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2 mt-3">
                          <a
                            href={waLink("", buildWeekMessage(w))}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Button
                              size="sm"
                              className="bg-[#25D366] hover:bg-[#1DA851] text-white"
                              data-testid={`weekly-wa-share-${w.week_start}`}
                            >
                              <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> Share on WhatsApp
                            </Button>
                          </a>
                          <a
                            href={exportPivotXlsxUrl({
                              start_date: w.week_start,
                              end_date: w.week_end,
                              group_by: "day",
                            })}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Button size="sm" variant="outline" data-testid={`weekly-pivot-${w.week_start}`}>
                              <Grid3x3 className="h-3.5 w-3.5 mr-1.5" /> Pivot (daily)
                            </Button>
                          </a>
                          <a
                            href={exportXlsxUrl({ start_date: w.week_start, end_date: w.week_end })}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Button size="sm" variant="outline" data-testid={`weekly-xlsx-${w.week_start}`}>
                              <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Excel
                            </Button>
                          </a>
                          <a
                            href={exportCsvUrl({ start_date: w.week_start, end_date: w.week_end })}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Button size="sm" variant="outline" data-testid={`weekly-csv-${w.week_start}`}>
                              <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
                            </Button>
                          </a>
                        </div>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 ${className}`}>
    {children}
  </th>
);

const StatCard = ({ label, value, tone = "default", isCurrency = true }) => {
  const cls = {
    default: "text-zinc-950",
    positive: "text-emerald-700",
    warning: "text-amber-700",
    danger: "text-rose-600",
  }[tone];
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-2 font-display text-xl md:text-2xl font-semibold tabular ${cls}`}>
        {isCurrency ? formatINR(value) : value}
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, tone = "default" }) => {
  const cls = {
    default: "text-zinc-900",
    positive: "text-emerald-700",
    warning: "text-amber-700",
  }[tone];
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-sm font-semibold tabular ${cls}`}>{formatINR(value)}</div>
    </div>
  );
};

const PmChip = ({ label, value, tint }) => (
  <div className={`rounded-lg border px-3 py-2 ${tint}`}>
    <div className="text-[10px] uppercase tracking-widest font-semibold">{label}</div>
    <div className="font-display text-base font-semibold tabular">{formatINR(value || 0)}</div>
  </div>
);
